// AddSubcategory.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { X, Eye, EyeSlash } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import {
  getCandidatesToSubcategory,
  addSubCategoryToCategory
} from '../../../../../../../../../../crud/menu_api/categories';
import MiniLoadingSpinner from '../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './AddSubcategory.scss';

interface SubCategory {
  id: number;
  name: string;
  isVisible: boolean;
}

interface AddSubCategoryProps {
  show: boolean;
  handleClose: () => void;
  category: any;
  setSelectedCategory: (cat: any) => void;
  categories: any[];
  setCategories: (cats: any[]) => void;
  onSubcategoryAdded?: (sub: SubCategory) => void;
}

const AddSubCategory: React.FC<AddSubCategoryProps> = ({
  show,
  handleClose,
  category,
  setSelectedCategory,
  categories,
  setCategories,
  onSubcategoryAdded,
}) => {
  const [availableSubCategories, setAvailableSubCategories] = useState<SubCategory[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Derive the selected object for easier use
  const selectedSubCategory = availableSubCategories.find(sc => sc.id === selectedId) || null;

  // Fetch candidates, excluding ones already in this category
  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const userContext = localStorage.getItem('user');
      const localId = userContext ? JSON.parse(userContext).local_id : 0;
      const res = await getCandidatesToSubcategory(localId);
      
      if (res?.data) {
        const raw: Record<string, string> = res.data;
        const allSubs: SubCategory[] = Object.entries(raw).map(([id, name]) => ({
          id: Number(id),
          name,
          isVisible: true, // Default to visible - could be enhanced with actual API data
        }));

        setAvailableSubCategories(allSubs);
      }
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setAvailableSubCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      setSelectedId(null);
      fetchOptions();
    }
  }, [show, fetchOptions]);

  const handleSelect = (key: string | null) => {
    setSelectedId(key ? Number(key) : null);
  };


  const handleAdd = async () => {
    if (!selectedSubCategory) {
      toast.warning('Por favor selecciona una subcategoría.');
      return;
    }
    
    setLoading(true);
    try {
      await addSubCategoryToCategory(category.id, selectedSubCategory.id);
      toast.success('Subcategoría agregada a la categoría con éxito!');

      // Update parent state
      const updatedSubs = [
        ...(category.subCategories || []),
        selectedSubCategory
      ];
      setSelectedCategory({ ...category, subCategories: updatedSubs });
      setCategories(
        categories.map(cat =>
          cat.id === category.id
            ? { ...cat, subCategories: updatedSubs }
            : cat
        )
      );
      onSubcategoryAdded?.(selectedSubCategory);
      // reload the page
      window.location.reload();
    } catch (err) {
      console.error('Error al agregar subcategoría:', err);
      toast.error('Error al agregar la subcategoría a la categoría');
    } finally {
      setLoading(false);
    }
  };

  const renderCreateFromExisting = () => {
    return (
      <div className="form-container">
        <p>
          Selecciona una subcategoría para agregar a la categoría{' '}
          <strong>
            {category?.category_name || category?.name}
          </strong>:
        </p>

        {/* Legend explaining the icons */}
        <div className="visibility-legend">
          <h6>Leyenda de visibilidad:</h6>
          <div className="legend-items">
            <div className="legend-item">
              <Eye className="legend-icon visible" />
              <span>Visible para clientes</span>
            </div>
            <div className="legend-item">
              <EyeSlash className="legend-icon hidden" />
              <span>Oculta para clientes</span>
            </div>
          </div>
        </div>

        <Form.Group className="mp-formgroup horizontal">
          <Form.Label>Subcategoría</Form.Label>
          <Dropdown onSelect={handleSelect}>
            <Dropdown.Toggle
              variant="light"
              id="dropdown-subcategory"
              className="dropdown-toggle"
            >
              {selectedSubCategory ? (
                <div className="selected-subcategory-display">
                  {selectedSubCategory.isVisible ? (
                    <Eye className="subcategory-icon visible" />
                  ) : (
                    <EyeSlash className="subcategory-icon hidden" />
                  )}
                  <span>{selectedSubCategory.name}</span>
                </div>
              ) : (
                'Seleccionar subcategoría'
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {availableSubCategories.length === 0 ? (
                <Dropdown.Item disabled>
                  No hay subcategorías disponibles
                </Dropdown.Item>
              ) : (
                availableSubCategories.map(sub => (
                  <Dropdown.Item
                    key={sub.id}
                    eventKey={sub.id.toString()}
                    className="subcategory-dropdown-item"
                  >
                    <div className="subcategory-item-content">
                      {sub.isVisible ? (
                        <Eye className="subcategory-icon visible" />
                      ) : (
                        <EyeSlash className="subcategory-icon hidden" />
                      )}
                      <span className="subcategory-name">{sub.name}</span>
                    </div>
                  </Dropdown.Item>
                ))
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Form.Group>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="add-subcategory-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Agregar Subcategoría a la Categoría
          </Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      {loading && (
        <div className="loading-overlay">
          <MiniLoadingSpinner />
        </div>
      )}

      {!loading && (
        <Modal.Body>
          {renderCreateFromExisting()}
        </Modal.Body>
      )}

      <Modal.Footer>
        <Button
          variant="none"
          onClick={handleClose}
          className="form-button-cancel"
        >
          Cancelar
        </Button>
        <Button
          variant="none"
          onClick={handleAdd}
          disabled={loading || !selectedSubCategory}
          className="form-button-create"
        >
          Agregar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddSubCategory;
